"use client"

import React, { useEffect, useMemo } from "react"
import cx from "classnames"
import { useFreezeBodyScroll } from "hooks"
import { Message } from "@locmod/intl"
import { useMedia, useSxMarketFilter } from "@/contexts"
import { Icon } from "components/ui"
import { Overlay } from "components/layout"
import messages from "./messages"
import SxMarket from "./components/Market/SxMarket"
import useChainProviderHook from "@/hooks/useChainProvider"
import { transformSxGameForMarkets } from "@/SX/utils/transformSxMarkets"

type MarketProps = {
  market: any
  game: any
  section?: string
  marketName?: string
  isFirstGame?: boolean
}

const HeadMarket: React.FC<MarketProps> = ({
  game,
  market,
  isFirstGame = false,
}) => {
  const { marketName, orders } = market[0]
  return (
    <SxMarket
      market={orders}
      game={game}
      section="Main Markets"
      marketName={marketName}
      isFirstGame={isFirstGame}
    />
  )
}

type OtherMarketsProps = {
  marketName: string
  game: any
  market: any
  isFirstGame?: boolean
}

const OtherMarkets: React.FC<OtherMarketsProps> = ({
  marketName,
  game,
  market,
  isFirstGame = false,
}) => {
  return (
    <SxMarket
      market={market}
      game={game}
      marketName={marketName}
      section="All Markets"
      isFirstGame={isFirstGame}
    />
  )
}

type MobileMarketsProps = {
  markets: any[]
  game: any
  onClose: () => void
}

const MobileMarkets: React.FC<MobileMarketsProps> = ({
  markets,
  game,
  onClose,
}) => {
  useFreezeBodyScroll()

  const handleContentClick = (event: React.MouseEvent) => {
    event.stopPropagation()
  }

  return (
    <Overlay className="flex items-end" onClick={onClose}>
      <div
        className="relative w-full bg-accent-grey-1000 rounded-t-min px-4 pb-4 max-h-[70vh] overflow-auto no-scrollbar"
        onClick={handleContentClick}
      >
        <div className="flex items-center justify-between p-4 -mx-4 bg-accent-grey-1000 sticky top-0 z-10">
          <Message
            className="text-caption-13 font-semibold"
            value={messages.markets}
          />
          <button
            className="size-4 text-accent-grey-30"
            onClick={onClose}
            aria-label="Close markets"
          >
            <Icon name="interface/close" />
          </button>
        </div>
        {markets.slice(1).map(({ marketName, orders }) => (
          <OtherMarkets
            key={marketName}
            marketName={marketName}
            game={game}
            market={orders}
          />
        ))}
      </div>
    </Overlay>
  )
}

export const MarketsSkeleton: React.FC = () => {
  return (
    <div className="ds:p-2 border border-transparent">
      <div className="flex">
        <div className="market-skeleton" />
        <div className="bone size-7 mt-5 ml-2 rounded-min flex-none border border-transparent" />
      </div>
    </div>
  )
}

type ContentProps = {
  markets: { marketName: string; orders: any[] }[]
  game: any
  isFirstGame?: boolean
}

const Content: React.FC<ContentProps> = ({
  markets,
  game,
  isFirstGame = false,
}) => {
  const [isOpen, setOpen] = React.useState(false)
  const { isMobileView } = useMedia()
  const contentRef = React.useRef<HTMLDivElement>(null)

  // Find the first market that has orders with valid odds
  const headMarket = markets.find(
    market =>
      market.orders &&
      market.orders.length > 0 &&
      market.orders.some(
        order => order.odds !== null && order.odds !== undefined
      )
  )

  // Get all other markets (excluding the head market)
  const otherMarkets = markets.filter(
    market => market.marketName !== headMarket?.marketName
  )

  const isDisabled = markets.length <= 1

  const contentClassName = cx(
    "w-full flex mb:border-transparent ds:p-2 border",
    {
      "absolute overflow-y-auto no-scrollbar max-h-[20rem] border-accent-grey-800 pb-2 z-30 rounded-md bg-accent-grey-950":
        isOpen && !isMobileView,
      "border-transparent": !isOpen,
    }
  )

  const buttonClassName = cx(
    "size-7 flex items-center justify-center flex-none rounded-min",
    "group border-[1px] border-accent-grey-700 text-accent-grey-100 ml-2 mt-5 sticky top-5",
    "disabled:cursor-not-allowed disabled:text-accent-grey-700 disabled:border-accent-grey-800",
    {
      "hover:text-accent-grey-10 hover:border-accent-grey-400": !isDisabled,
    }
  )

  const handleClose = () => {
    setOpen(false)
  }

  const handleToggle = () => {
    setOpen(prev => !prev)
  }

  useMemo(() => {
    if (!isMobileView && !isOpen && contentRef.current) {
      contentRef.current.scrollTop = 0
    }
  }, [isOpen, isMobileView])

  useEffect(() => {
    if (isMobileView && !isOpen) {
      return
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const composedPath = event.composedPath()

      if (!composedPath.includes(contentRef.current!)) {
        setOpen(false)
      }
    }

    document.addEventListener("click", handleOutsideClick, { capture: true })

    return () => {
      document.removeEventListener("click", handleOutsideClick, {
        capture: true,
      })
    }
  }, [isOpen, isMobileView])

  return (
    <div className="w-full ds:relative ds:h-16">
      <div ref={contentRef} className={contentClassName}>
        <div className="w-full h-fit">
          {headMarket && (
            <HeadMarket
              game={game}
              market={[headMarket]}
              isFirstGame={isFirstGame}
            />
          )}
          {isOpen && (
            <>
              {isMobileView ? (
                <MobileMarkets
                  markets={otherMarkets}
                  game={game}
                  onClose={handleClose}
                />
              ) : (
                // Show all markets except the one displayed in header
                otherMarkets.map(({ marketName, orders }) => (
                  <OtherMarkets
                    key={`${marketName}`}
                    marketName={marketName}
                    game={game}
                    market={orders}
                    isFirstGame={isFirstGame}
                  />
                ))
              )}
            </>
          )}
        </div>
        {otherMarkets.length > 0 && (
          <button
            className={buttonClassName}
            disabled={isDisabled}
            onClick={handleToggle}
          >
            <Icon
              className={cx("size-4", {
                "rotate-180 text-accent-grey-30": isOpen,
              })}
              name="interface/chevron_down"
            />
          </button>
        )}
      </div>
    </div>
  )
}

type MarketsProps = {
  game: any
  showFilter?: boolean
  isFirstGame?: boolean
}

const SxMarkets: React.FC<MarketsProps> = ({
  game,
  showFilter = false,
  isFirstGame = false,
}) => {
  const { chainId } = useChainProviderHook()
  const { isSxMarketTypeVisible, selectedMarkets } = useSxMarketFilter()

  // Transform game data using the utility function
  const { processedMarkets } = useMemo(
    () => transformSxGameForMarkets(game, chainId),
    [game, chainId]
  )

  // Filter markets based on selected filters (only when showFilter is true)
  const filteredMarkets = useMemo(() => {
    if (!processedMarkets.length) {
      return []
    }

    // If filter is not shown, return all markets without filtering
    if (!showFilter) {
      return processedMarkets
    }

    // If no filters are selected, show all markets
    if (selectedMarkets.length === 0) {
      return processedMarkets
    }

    return processedMarkets.filter(market => {
      // Check if market type is visible based on selected filters
      return isSxMarketTypeVisible(market.marketName)
    })
  }, [processedMarkets, isSxMarketTypeVisible, selectedMarkets, showFilter])

  // Find the best head market from filtered markets
  const findBestHeadMarket = useMemo(() => {
    if (!filteredMarkets.length) {
      return null
    }

    // If filter is not shown, use the first available market
    if (!showFilter) {
      return filteredMarkets.find(
        market =>
          market.orders &&
          market.orders.length > 0 &&
          market.orders.some(
            order => order.odds !== null && order.odds !== undefined
          )
      )
    }

    // Try to find a market that matches the first selected filter
    for (const filterValue of selectedMarkets) {
      const matchingMarket = filteredMarkets.find(market =>
        isSxMarketTypeVisible(market.marketName)
      )
      if (matchingMarket) {
        return matchingMarket
      }
    }

    // Fallback to first available filtered market
    return filteredMarkets[0]
  }, [filteredMarkets, showFilter, selectedMarkets, isSxMarketTypeVisible])

  // Get other markets (excluding the head market)
  const otherMarkets = useMemo(() => {
    if (!findBestHeadMarket) {
      return []
    }

    return filteredMarkets.filter(
      market => market.marketName !== findBestHeadMarket.marketName
    )
  }, [filteredMarkets, findBestHeadMarket])

  if (!findBestHeadMarket) {
    return null
  }

  return (
    <Content
      markets={[findBestHeadMarket, ...otherMarkets]}
      game={game}
      isFirstGame={isFirstGame}
    />
  )
}

export default SxMarkets
